'use client';

import { c as _c } from "react/compiler-runtime";
import { useAtomValue } from 'jotai';
import { usePathname as useUrlPathname } from 'next/navigation';
import { pageTypeOverrideAtom } from '@/atoms/pageTypeAtom';
import { usePathname } from '@/i18n/navigation';
import { PageChrome, derivePageChrome } from '@/utility/pageChrome';

/**
 * Chrome for the current page (which header nav level is active, whether the
 * promo banner and chat button show). Derived from the route, except where a
 * page declares its page type or hides the promo banner explicitly via
 * `PageTypeSetter`.
 *
 * An override only counts for the URL it was set for. The shell stays mounted
 * across navigations, so honouring it unconditionally would keep the previous
 * page's Amplience page type after leaving a CMS page — long enough to highlight
 * "Create" on a `/shop` URL — until `PageTypeSetter`'s effect cleanup cleared it.
 */
export const usePageChrome = () => {
  const $ = _c(10);
  const route = usePathname();
  const url = useUrlPathname();
  const pageTypeOverride = useAtomValue(pageTypeOverrideAtom);
  let t0;
  if ($[0] !== route) {
    t0 = derivePageChrome(route);
    $[0] = route;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const chrome = t0;
  if (pageTypeOverride?.pathname !== url) {
    return chrome;
  }
  let t1;
  if ($[2] !== pageTypeOverride.pageType) {
    t1 = pageTypeOverride.pageType && {
      pageType: pageTypeOverride.pageType
    };
    $[2] = pageTypeOverride.pageType;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== pageTypeOverride.hidePromoBanner) {
    t2 = pageTypeOverride.hidePromoBanner !== undefined && {
      hidePromoBanner: pageTypeOverride.hidePromoBanner
    };
    $[4] = pageTypeOverride.hidePromoBanner;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== chrome || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      ...chrome,
      ...t1,
      ...t2
    };
    $[6] = chrome;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};